import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerWorker, listWorkforceSkills } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const RED = "#A32D2D";

/**
 * Public sign-up for workers.  No password / no OTP in Phase 1 — the browser
 * that submits the form gets an HttpOnly cookie back and that's how they'll
 * edit their profile later (from the same browser).  Cross-device sign-in
 * is Phase 2.
 */
export default function AvailableTodayJoinPage() {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    city: "", state: "", max_travel_miles: 20,
    transport: "own_vehicle", languages: [], years_experience: 0,
    hourly_rate_usd: 25, resume_url: "", references: [],
    osha_certified: false, licenses: [], skills: [],
    availability_buckets: ["today"], availability_schedule: [],
    work_preferences: [], work_i_wont_do: [],
    photo_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    listWorkforceSkills().then((d) => setSkills(d.items || [])).catch(() => {});
  }, []);

  const toggleSkill = (s) => {
    setForm((f) => f.skills.includes(s)
      ? { ...f, skills: f.skills.filter((x) => x !== s) }
      : { ...f, skills: [...f.skills, s] });
  };

  const toggleBucket = (b) => {
    setForm((f) => f.availability_buckets.includes(b)
      ? { ...f, availability_buckets: f.availability_buckets.filter((x) => x !== b) }
      : { ...f, availability_buckets: [...f.availability_buckets, b] });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      setError("Name, phone, and email are all required.");
      return;
    }
    if (form.skills.length === 0) {
      setError("Pick at least one skill so contractors can find you.");
      return;
    }
    setBusy(true); setError(null);
    try {
      const listify = (v) => Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        languages: listify(form.languages),
        references: listify(form.references),
        licenses: listify(form.licenses),
        work_preferences: listify(form.work_preferences),
        work_i_wont_do: listify(form.work_i_wont_do),
      };
      await registerWorker(payload);
      navigate("/available-today/me", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="available-today-join-page">
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>AVAILABLE TODAY · WORKER SIGN-UP · FREE</div>
        <h1 style={h1}>JOIN THE NEARNOW WORKFORCE</h1>
        <p style={{ color: "#5b5e69", marginBottom: 30, fontSize: 15 }}>
          Free forever for workers. Fill this out once and contractors on the $149+ NearNow plan can find you the moment they need help. Toggle yourself on and off any time.
        </p>

        <form onSubmit={submit} style={card}>
          <Section title="THE BASICS">
            <Row><Field label="Full name *"><TextInput value={form.name} onChange={(v) => setForm({...form, name: v})} testid="worker-name" required /></Field></Row>
            <Row>
              <Field label="Phone *"><TextInput value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="worker-phone" placeholder="401-555-0100" required /></Field>
              <Field label="Email *"><TextInput type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="worker-email" required /></Field>
            </Row>
            <Row>
              <Field label="City"><TextInput value={form.city} onChange={(v) => setForm({...form, city: v})} testid="worker-city" placeholder="Providence" /></Field>
              <Field label="State"><TextInput value={form.state} onChange={(v) => setForm({...form, state: v.toUpperCase().slice(0,2)})} testid="worker-state" placeholder="RI" style={{maxWidth: 90}} /></Field>
              <Field label="Max travel (mi)"><TextInput type="number" value={form.max_travel_miles} onChange={(v) => setForm({...form, max_travel_miles: v})} testid="worker-max-travel" style={{maxWidth: 120}} /></Field>
            </Row>
            <Row>
              <Field label="Transportation">
                <select value={form.transport} onChange={(e) => setForm({...form, transport: e.target.value})} style={inputStyle} data-testid="worker-transport">
                  <option value="own_vehicle">Own vehicle</option>
                  <option value="own_truck">Own truck</option>
                  <option value="carpool">Carpool</option>
                  <option value="public_transit">Public transit</option>
                  <option value="walk">Walk / bike</option>
                  <option value="needs_ride">Needs a ride</option>
                </select>
              </Field>
              <Field label="Languages (comma-sep)"><TextInput value={form.languages} onChange={(v) => setForm({...form, languages: v})} testid="worker-languages" placeholder="English, Spanish" /></Field>
            </Row>
          </Section>

          <Section title="EXPERIENCE & RATE">
            <Row>
              <Field label="Years experience"><TextInput type="number" value={form.years_experience} onChange={(v) => setForm({...form, years_experience: v})} testid="worker-years" style={{maxWidth: 120}} /></Field>
              <Field label="Hourly rate ($)"><TextInput type="number" value={form.hourly_rate_usd} onChange={(v) => setForm({...form, hourly_rate_usd: v})} testid="worker-hourly" style={{maxWidth: 140}} /></Field>
              <Field label="OSHA certified">
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 0" }}>
                  <input type="checkbox" checked={form.osha_certified} onChange={(e) => setForm({...form, osha_certified: e.target.checked})} data-testid="worker-osha" style={{ width: 22, height: 22, accentColor: NAVY }} />
                  Yes, OSHA card
                </label>
              </Field>
            </Row>
            <Row><Field label="Trade licenses (comma-sep)"><TextInput value={form.licenses} onChange={(v) => setForm({...form, licenses: v})} testid="worker-licenses" placeholder="RI Electrician B, MA Plumbing" /></Field></Row>
            <Row><Field label="Resume URL (optional)"><TextInput value={form.resume_url} onChange={(v) => setForm({...form, resume_url: v})} testid="worker-resume" placeholder="https://…" /></Field></Row>
            <Row><Field label="References (comma-sep, name + phone)"><TextInput value={form.references} onChange={(v) => setForm({...form, references: v})} testid="worker-references" /></Field></Row>
          </Section>

          <Section title="SKILLS · pick everything you're comfortable with">
            <div style={pillGrid} data-testid="skill-pills">
              {skills.map((s) => (
                <button type="button" key={s.id} onClick={() => toggleSkill(s.name)}
                  data-testid={`skill-${s.slug}`}
                  style={{
                    padding: "10px 14px", borderRadius: 22,
                    border: `1.5px solid ${form.skills.includes(s.name) ? NAVY : "#d0d3da"}`,
                    background: form.skills.includes(s.name) ? NAVY : "#fff",
                    color: form.skills.includes(s.name) ? GOLD : NAVY,
                    fontSize: 13, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer",
                  }}>
                  {s.name}
                </button>
              ))}
            </div>
          </Section>

          <Section title="AVAILABILITY">
            <Row>
              {["today", "this_week", "weekends", "evenings"].map((b) => (
                <button type="button" key={b} onClick={() => toggleBucket(b)}
                        data-testid={`bucket-${b}`}
                  style={{
                    padding: "12px 18px", borderRadius: 22,
                    border: `1.5px solid ${form.availability_buckets.includes(b) ? NAVY : "#d0d3da"}`,
                    background: form.availability_buckets.includes(b) ? NAVY : "#fff",
                    color: form.availability_buckets.includes(b) ? GOLD : NAVY,
                    fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: "pointer", flex: "0 0 auto",
                  }}>
                  {b === "today" && "🟢 AVAILABLE TODAY"}
                  {b === "this_week" && "This Week"}
                  {b === "weekends" && "Weekends"}
                  {b === "evenings" && "Evenings"}
                </button>
              ))}
            </Row>
          </Section>

          <Section title="PREFERENCES">
            <Row><Field label="Work I like (comma-sep)"><TextInput value={form.work_preferences} onChange={(v) => setForm({...form, work_preferences: v})} testid="worker-prefs" placeholder="commercial, residential remodels" /></Field></Row>
            <Row><Field label="Work I won't do (comma-sep)"><TextInput value={form.work_i_wont_do} onChange={(v) => setForm({...form, work_i_wont_do: v})} testid="worker-wont" placeholder="heights, asbestos" /></Field></Row>
          </Section>

          {error && <div data-testid="join-error" style={errBox}>{error}</div>}

          <button type="submit" disabled={busy} data-testid="join-submit-button" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
            {busy ? "Creating your profile…" : "PUBLISH MY PROFILE →"}
          </button>
          <p style={{ color: "#5b5e69", fontSize: 12, marginTop: 12 }}>
            By joining you agree contractors on NearNow's paid plans can view your profile and reach out. You can toggle 🔴 Unavailable any time.
          </p>
          <p style={{ marginTop: 20 }}>
            <Link to="/available-today" style={{ color: NAVY, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
              ← Back to Available Today
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 26 }}>
    <div style={{ color: NAVY, letterSpacing: 2, fontSize: 12, fontWeight: 700, marginBottom: 12, borderBottom: `2px solid ${GOLD}`, paddingBottom: 6 }}>{title}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>{children}</div>;
const Field = ({ label, children }) => (
  <div style={{ flex: 1, minWidth: 180 }}>
    <div style={{ color: "#5b5e69", fontSize: 11, letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);
const TextInput = ({ value, onChange, type = "text", testid, placeholder, style, required }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
         placeholder={placeholder} required={required}
         data-testid={testid} style={{ ...inputStyle, ...(style || {}) }} />
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const card = { background: "#fff", borderRadius: 18, padding: "32px 30px", border: "1px solid #e5e7ee", boxShadow: "0 20px 40px -20px rgba(10,22,40,0.12)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 44, color: NAVY, margin: "6px 0 14px" };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box", background: "#fff" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "18px 24px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 12 };
const errBox = { color: RED, background: "#FDECEC", padding: "12px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
const pillGrid = { display: "flex", flexWrap: "wrap", gap: 8 };
